import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
  Typography,
  Box,
  Alert,
  AlertTitle,
  CircularProgress,
  Chip,
  Divider,
  TextField,
  LinearProgress,
} from '@mui/material';
import http from '../../../utils/http';
import { errorToastMessage } from '../../../utils/toast';

interface RedactionPreviewHeader {
  participantId: string;
  participantName: string;
  participantEmail: string;
  studyName?: string;
}

interface RedactionPreviewRow {
  slNo: number;
  fieldName: string;
  fieldValue: string;
  action: 'REDACTED' | 'TRUNCATED' | 'BLURRED';
  revivalPossible: string;
  table: string;
}

interface RedactionPreviewResponse {
  header: RedactionPreviewHeader;
  rows: RedactionPreviewRow[];
  totalRows: number;
  reversibleCount: number;
  permanentCount: number;
}

interface Props {
  open: boolean;
  onClose: () => void;
  /** Registry-redact preview is fetched for each of these participants. */
  participantIds: string[];
  /** Called with the reason once the admin confirms. Caller runs bulk-redact. */
  onConfirm: (reason: string) => Promise<void>;
}

type ModalState = 'loading' | 'loaded' | 'confirming' | 'error';

interface ParticipantPreview {
  id: string;
  header: RedactionPreviewHeader;
  /** value keyed by `${table}::${fieldName}` */
  valueByKey: Record<string, string>;
}

interface FieldDescriptor {
  key: string;
  slNo: number;
  fieldName: string;
  table: string;
  action: string;
  revivalPossible: string;
  revivable: boolean;
}

const fieldKey = (r: RedactionPreviewRow) => `${r.table}::${r.fieldName}`;

/**
 * Bulk registry-redaction preview. Mirrors the single-participant
 * RedactionPreviewModal but transposes the matrix: each field is a row, each
 * selected participant is a column, so the field set (and which fields are
 * revivable vs permanent) is shown once while values are compared across
 * participants. Participants whose preview is rejected by the backend (e.g.
 * a study enrolment is not yet redacted) are listed as blocked.
 */
const BulkRedactPreviewModal: React.FC<Props> = ({
  open,
  onClose,
  participantIds,
  onConfirm,
}) => {
  const [state, setState] = useState<ModalState>('loading');
  const [reason, setReason] = useState('');
  const [previews, setPreviews] = useState<ParticipantPreview[]>([]);
  const [fields, setFields] = useState<FieldDescriptor[]>([]);
  const [blocked, setBlocked] = useState<Array<{ id: string; error: string }>>(
    [],
  );

  const loadPreviews = async () => {
    setState('loading');
    setPreviews([]);
    setFields([]);
    setBlocked([]);
    try {
      const results = await Promise.allSettled(
        participantIds.map((id) =>
          http
            .get(`/admin/participants/${id}/redact/preview`)
            .then((r: any) => ({ id, data: r.data.data as RedactionPreviewResponse })),
        ),
      );

      const ok: ParticipantPreview[] = [];
      const failed: Array<{ id: string; error: string }> = [];
      // Preserve field order/metadata using the first successful preview, then
      // union in any field a later participant has that the first did not.
      const fieldMap = new Map<string, FieldDescriptor>();

      results.forEach((res, idx) => {
        const id = participantIds[idx];
        if (res.status === 'fulfilled' && res.value.data) {
          const data = res.value.data;
          const valueByKey: Record<string, string> = {};
          for (const row of data.rows) {
            const key = fieldKey(row);
            valueByKey[key] = row.fieldValue;
            if (!fieldMap.has(key)) {
              fieldMap.set(key, {
                key,
                slNo: row.slNo,
                fieldName: row.fieldName,
                table: row.table,
                action: row.action,
                revivalPossible: row.revivalPossible,
                revivable: row.revivalPossible.startsWith('Yes'),
              });
            }
          }
          ok.push({ id, header: data.header, valueByKey });
        } else {
          const err: any =
            res.status === 'rejected' ? res.reason : new Error('Empty preview');
          const msg =
            err?.response?.data?.message || err?.message || 'Unknown error';
          failed.push({ id, error: msg });
        }
      });

      const orderedFields = [...fieldMap.values()].sort((a, b) => {
        // reversible first, then by original slNo
        if (a.revivable !== b.revivable) return a.revivable ? -1 : 1;
        return a.slNo - b.slNo;
      });

      setPreviews(ok);
      setFields(orderedFields);
      setBlocked(failed);
      setState('loaded');
    } catch (err) {
      setState('error');
      errorToastMessage(err as Error);
    }
  };

  useEffect(() => {
    if (open) {
      setReason('');
      loadPreviews();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleConfirm = async () => {
    setState('confirming');
    try {
      await onConfirm(reason.trim());
      onClose();
    } catch (err) {
      setState('loaded');
      errorToastMessage(err as Error);
    }
  };

  const handleClose = () => {
    if (state === 'confirming') return;
    onClose();
  };

  const isConfirming = state === 'confirming';
  const reversibleCount = fields.filter((f) => f.revivable).length;
  const permanentCount = fields.length - reversibleCount;
  // All-or-nothing: if ANY selected participant is ineligible, the whole batch
  // is blocked — the admin must deselect the offending participant(s) and retry.
  const hasBlocked = blocked.length > 0;
  const isConfirmDisabled =
    state !== 'loaded' ||
    reason.trim().length === 0 ||
    previews.length === 0 ||
    hasBlocked;

  const labelFor = (p: ParticipantPreview) =>
    p.header.participantName?.trim() || p.header.participantEmail || p.id;

  // Frozen-column geometry. Field / Action / Revival stay pinned to the left
  // while the participant value columns scroll horizontally beneath them.
  const COL_FIELD = 180;
  const COL_ACTION = 120;
  const COL_REVIVAL = 120;
  const frozenCell = (left: number, width: number, isHeader: boolean) => ({
    position: 'sticky' as const,
    left,
    minWidth: width,
    width,
    bgcolor: 'background.paper',
    zIndex: isHeader ? 3 : 1,
    ...(isHeader ? { top: 0 } : {}),
  });

  const renderFieldRow = (f: FieldDescriptor) => (
    <TableRow key={f.key}>
      <TableCell sx={{ ...frozenCell(0, COL_FIELD, false), fontWeight: 500 }}>
        {f.fieldName}
        <Typography variant="caption" color="text.secondary" display="block">
          {f.table}
        </Typography>
      </TableCell>
      <TableCell sx={frozenCell(COL_FIELD, COL_ACTION, false)}>
        {f.action}
      </TableCell>
      <TableCell sx={frozenCell(COL_FIELD + COL_ACTION, COL_REVIVAL, false)}>
        <Chip
          label={f.revivable ? 'Revivable' : 'Permanent'}
          size="small"
          color={f.revivable ? 'success' : 'error'}
          variant="outlined"
        />
      </TableCell>
      {previews.map((p) => (
        <TableCell
          key={p.id}
          sx={{ fontFamily: 'monospace', fontSize: '0.72rem', minWidth: 140 }}
        >
          {p.valueByKey[f.key] ?? '—'}
        </TableCell>
      ))}
    </TableRow>
  );

  const reversibleFields = fields.filter((f) => f.revivable);
  const permanentFields = fields.filter((f) => !f.revivable);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{ sx: { minHeight: '70vh' } }}
    >
      <DialogTitle>Bulk Redaction Preview</DialogTitle>
      <DialogContent>
        <Alert severity="warning" sx={{ mb: 2 }}>
          You are about to redact <strong>{previews.length}</strong>{' '}
          participant(s). Revivable fields are recoverable from escrow within the
          grace period; permanent fields cannot be restored. This action cannot
          be fully undone.
        </Alert>

        {state === 'loading' && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        )}

        {state === 'error' && (
          <Box sx={{ textAlign: 'center', py: 3 }}>
            <Alert severity="error" sx={{ mb: 2 }}>
              Failed to load preview. Please try again.
            </Alert>
            <Button variant="outlined" onClick={loadPreviews}>
              Retry
            </Button>
          </Box>
        )}

        {(state === 'loaded' || state === 'confirming') && (
          <>
            {hasBlocked && (
              <Alert severity="error" sx={{ mb: 2 }}>
                <AlertTitle>
                  {blocked.length} of {participantIds.length} selected
                  participant(s) cannot be redacted
                </AlertTitle>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  Redaction is all-or-nothing — no participants will be redacted
                  until you deselect the participant(s) below and try again.
                </Typography>
                {blocked.map((b) => (
                  <Typography key={b.id} variant="body2">
                    • {b.id}: {b.error}
                  </Typography>
                ))}
              </Alert>
            )}

            {isConfirming && (
              <Box sx={{ mb: 2 }}>
                <LinearProgress />
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ mt: 0.5, display: 'block' }}
                >
                  Redacting participant data — this may take a few seconds.
                </Typography>
              </Box>
            )}

            <TextField
              label="Reason for redaction"
              required
              fullWidth
              multiline
              minRows={2}
              maxRows={4}
              value={reason}
              onChange={(e) => setReason(e.target.value.slice(0, 1000))}
              disabled={isConfirming}
              error={reason.trim().length === 0}
              helperText={
                reason.trim().length === 0
                  ? 'Required — recorded in the audit trail for every participant.'
                  : `${reason.length}/1000`
              }
              inputProps={{ maxLength: 1000 }}
              sx={{ mb: 2 }}
            />

            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ mb: 1, display: 'block' }}
            >
              {reversibleCount} revivable · {permanentCount} permanent field(s) ·{' '}
              {previews.length} participant(s)
            </Typography>

            {previews.length === 0 ? (
              <Alert severity="info">
                No participants are eligible for redaction in this selection.
              </Alert>
            ) : (
              <Box sx={{ maxHeight: 420, overflow: 'auto' }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={frozenCell(0, COL_FIELD, true)}>
                        Field
                      </TableCell>
                      <TableCell sx={frozenCell(COL_FIELD, COL_ACTION, true)}>
                        Action
                      </TableCell>
                      <TableCell
                        sx={frozenCell(COL_FIELD + COL_ACTION, COL_REVIVAL, true)}
                      >
                        Revival
                      </TableCell>
                      {previews.map((p) => (
                        <TableCell key={p.id} sx={{ minWidth: 140 }}>
                          {labelFor(p)}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {reversibleFields.map(renderFieldRow)}
                    {reversibleFields.length > 0 &&
                      permanentFields.length > 0 && (
                        <TableRow>
                          <TableCell colSpan={3 + previews.length} sx={{ p: 0 }}>
                            <Divider />
                          </TableCell>
                        </TableRow>
                      )}
                    {permanentFields.map(renderFieldRow)}
                  </TableBody>
                </Table>
              </Box>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isConfirming}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="error"
          onClick={handleConfirm}
          disabled={isConfirmDisabled}
          startIcon={
            isConfirming ? (
              <CircularProgress size={16} color="inherit" />
            ) : undefined
          }
        >
          {isConfirming ? 'Redacting…' : 'Confirm Redaction'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BulkRedactPreviewModal;
